// PPS CRM - Clients Module
import { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/auth';

interface Client {
  id: string;
  fullLegalName: string;
  email: string;
  phone: string;
  status: 'ACTIVE' | 'PENDING_APPROVAL' | 'INACTIVE' | 'REJECTED' | 'FLAGGED';
  packageType: 'BASIC' | 'STANDARD' | 'PREMIUM' | 'MAXIMUM';
  agent: { agentCode?: string; fullLegalName?: string };
  createdAt: string;
}

export default function Clients() {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [showModal, setShowModal] = useState(false);
  const { user } = useAuthStore();

  useEffect(() => {
    fetchClients();
  }, [statusFilter]);

  const fetchClients = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'ALL') params.append('status', statusFilter);

      const response = await api.get(`/api/clients?${params}`);
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = clients.filter((client) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      client.fullLegalName.toLowerCase().includes(searchLower) ||
      client.email.toLowerCase().includes(searchLower) ||
      (client.agent?.agentCode || '').toLowerCase().includes(searchLower)
    );
  });

  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return { bg: '#D1FAE5', text: '#166534' };
      case 'PENDING_APPROVAL':
        return { bg: '#FEF3C7', text: '#92400E' };
      case 'INACTIVE':
        return { bg: '#FEE2E2', text: '#991B1B' };
      case 'FLAGGED':
        return { bg: '#FEF3C7', text: '#92400E' };
      case 'REJECTED':
        return { bg: '#FEE2E2', text: '#991B1B' };
      default:
        return { bg: '#F3F4F6', text: '#6B7280' };
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold" style={{ color: '#111' }}>
          Clients
        </h2>
        <button
          onClick={() => setShowModal(true)}
          className="px-4 py-2 rounded-lg font-medium"
          style={{ backgroundColor: '#DC2626', color: '#fff', border: '1px solid #DC2626' }}
        >
          + New Client
        </button>
      </div>

      {/* Filters */}
      <div
        className="rounded-lg p-4 mb-6"
        style={{
          backgroundColor: '#fff',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #E5E7EB',
        }}
      >
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Search clients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 rounded-lg"
              style={{ border: '1px solid #D1D5DB', backgroundColor: '#F9FAFB' }}
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 rounded-lg"
            style={{ border: '1px solid #D1D5DB', backgroundColor: '#F9FAFB' }}
          >
            <option value="ALL">All Status</option>
            <option value="ACTIVE">Active</option>
            <option value="PENDING_APPROVAL">Pending Approval</option>
            <option value="INACTIVE">Inactive</option>
            <option value="FLAGGED">Flagged</option>
            <option value="REJECTED">Rejected</option>
          </select>
        </div>
      </div>

      {/* Clients Table */}
      <div
        className="rounded-lg overflow-hidden"
        style={{
          backgroundColor: '#fff',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #E5E7EB',
        }}
      >
        {loading ? (
          <div className="p-8 text-center" style={{ color: '#6B7280' }}>
            Loading...
          </div>
        ) : filteredClients.length === 0 ? (
          <div className="p-8 text-center" style={{ color: '#6B7280' }}>
            No clients found
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr style={{ backgroundColor: '#F9FAFB' }}>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase" style={{ color: '#6B7280' }}>
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase" style={{ color: '#6B7280' }}>
                  Email
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase" style={{ color: '#6B7280' }}>
                  Phone
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase" style={{ color: '#6B7280' }}>
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase" style={{ color: '#6B7280' }}>
                  Package
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase" style={{ color: '#6B7280' }}>
                  Agent
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase" style={{ color: '#6B7280' }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredClients.map((client) => {
                const statusStyle = getStatusStyle(client.status);
                return (
                  <tr key={client.id} className="border-t" style={{ borderColor: '#E5E7EB' }}>
                    <td className="px-4 py-3">
                      <span className="font-medium" style={{ color: '#111' }}>
                        {client.fullLegalName}
                      </span>
                    </td>
                    <td className="px-4 py-3" style={{ color: '#6B7280' }}>
                      {client.email}
                    </td>
                    <td className="px-4 py-3" style={{ color: '#6B7280' }}>
                      {client.phone || '-'}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className="px-2 py-1 rounded text-xs font-medium"
                        style={{ backgroundColor: statusStyle.bg, color: statusStyle.text }}
                      >
                        {client.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm" style={{ color: '#6B7280' }}>
                        {client.packageType}
                      </span>
                    </td>
                    <td className="px-4 py-3" style={{ color: '#6B7280' }}>
                      {client.agent?.agentCode || '-'}
                    </td>
                    <td className="px-4 py-3">
                      {(user?.role === 'OWNER' || user?.role === 'MANAGER') && client.status === 'PENDING_APPROVAL' && (
                        <button
                          onClick={async () => {
                            await api.patch(`/api/clients/${client.id}/approve`);
                            fetchClients();
                          }}
                          className="text-primary hover:underline text-sm"
                        >
                          Approve
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <ClientModal
          onClose={() => setShowModal(false)}
          onSave={() => {
            setShowModal(false);
            fetchClients();
          }}
        />
      )}
    </div>
  );
}

function ClientModal({ onClose, onSave }: { onClose: () => void; onSave: () => void }) {
  const [formData, setFormData] = useState({
    fullLegalName: '',
    dateOfBirth: '',
    ssn: '',
    phone: '',
    email: '',
    addressStreet: '',
    addressCity: '',
    addressState: '',
    addressZip: '',
    jobTitle: '',
    payPeriodStart: '',
    payPeriodEnd: '',
    grossPay: '',
    paymentType: 'HOURLY',
    hourlyRate: '',
    hoursWorked: '',
    ytdGross: '',
    ytdTaxes: '',
    depositMethod: 'DIRECT_DEPOSIT',
    bankName: '',
    routingNumber: '',
    accountNumber: '',
    accountType: 'CHECKING',
    digitalPlatform: '',
    digitalHandle: '',
    packageType: 'BASIC',
    depositAmount: '',
    serviceFee: '',
    rushAddon: false,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/api/clients', {
        ...formData,
        grossPay: Number(formData.grossPay),
        hourlyRate: formData.hourlyRate ? Number(formData.hourlyRate) : null,
        hoursWorked: formData.hoursWorked ? Number(formData.hoursWorked) : null,
        ytdGross: formData.ytdGross ? Number(formData.ytdGross) : null,
        ytdTaxes: formData.ytdTaxes ? Number(formData.ytdTaxes) : null,
        depositAmount: Number(formData.depositAmount),
        serviceFee: Number(formData.serviceFee),
      });
      onSave();
    } catch (error) {
      console.error('Error creating client:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="client-modal rounded-lg p-6 w-full max-w-5xl max-h-[90vh] overflow-y-auto" style={{ backgroundColor: '#fff' }}>
        <h3 className="text-xl font-bold mb-4" style={{ color: '#111' }}>
          New Client
        </h3>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Full Legal Name *</label>
              <input
                type="text"
                required
                value={formData.fullLegalName}
                onChange={(e) => setFormData({ ...formData, fullLegalName: e.target.value })}
                className="w-full input-compact"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Date of Birth *</label>
              <input
                type="date"
                required
                value={formData.dateOfBirth}
                onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                className="w-full input-compact"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">SSN *</label>
              <input
                type="text"
                required
                value={formData.ssn}
                onChange={(e) => setFormData({ ...formData, ssn: e.target.value })}
                className="w-full input-compact"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Phone *</label>
              <input
                type="tel"
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full input-compact"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email *</label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full input-compact"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Job Title *</label>
              <input
                type="text"
                required
                value={formData.jobTitle}
                onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
                className="w-full input-compact"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium mb-1">Address Street *</label>
              <input
                type="text"
                required
                value={formData.addressStreet}
                onChange={(e) => setFormData({ ...formData, addressStreet: e.target.value })}
                className="w-full input-compact"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">City *</label>
              <input
                type="text"
                required
                value={formData.addressCity}
                onChange={(e) => setFormData({ ...formData, addressCity: e.target.value })}
                className="w-full input-compact"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">State *</label>
              <input
                type="text"
                required
                value={formData.addressState}
                onChange={(e) => setFormData({ ...formData, addressState: e.target.value })}
                className="w-full input-compact"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Zip *</label>
              <input
                type="text"
                required
                value={formData.addressZip}
                onChange={(e) => setFormData({ ...formData, addressZip: e.target.value })}
                className="w-full input-compact"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium mb-1">Pay Period Start *</label>
              <input
                type="date"
                required
                value={formData.payPeriodStart}
                onChange={(e) => setFormData({ ...formData, payPeriodStart: e.target.value })}
                className="w-full input-compact"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Pay Period End *</label>
              <input
                type="date"
                required
                value={formData.payPeriodEnd}
                onChange={(e) => setFormData({ ...formData, payPeriodEnd: e.target.value })}
                className="w-full input-compact"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Gross Pay *</label>
              <input
                type="number"
                required
                value={formData.grossPay}
                onChange={(e) => setFormData({ ...formData, grossPay: e.target.value })}
                className="w-full input-compact"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Payment Type *</label>
              <select
                value={formData.paymentType}
                onChange={(e) => setFormData({ ...formData, paymentType: e.target.value })}
                className="w-full input-compact"
              >
                <option value="HOURLY">Hourly</option>
                <option value="SALARY">Salary</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Hourly Rate</label>
              <input
                type="number"
                value={formData.hourlyRate}
                onChange={(e) => setFormData({ ...formData, hourlyRate: e.target.value })}
                className="w-full input-compact"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Hours Worked</label>
              <input
                type="number"
                value={formData.hoursWorked}
                onChange={(e) => setFormData({ ...formData, hoursWorked: e.target.value })}
                className="w-full input-compact"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium mb-1">YTD Gross</label>
              <input
                type="number"
                value={formData.ytdGross}
                onChange={(e) => setFormData({ ...formData, ytdGross: e.target.value })}
                className="w-full input-compact"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">YTD Taxes</label>
              <input
                type="number"
                value={formData.ytdTaxes}
                onChange={(e) => setFormData({ ...formData, ytdTaxes: e.target.value })}
                className="w-full input-compact"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium mb-1">Deposit Method *</label>
              <select
                value={formData.depositMethod}
                onChange={(e) => setFormData({ ...formData, depositMethod: e.target.value })}
                className="w-full input-compact"
              >
                <option value="DIRECT_DEPOSIT">Direct Deposit</option>
                <option value="DIGITAL_WALLET">Digital Wallet</option>
                <option value="CHECK">Check</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Bank Name</label>
              <input
                type="text"
                value={formData.bankName}
                onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
                className="w-full input-compact"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Routing Number</label>
              <input
                type="text"
                value={formData.routingNumber}
                onChange={(e) => setFormData({ ...formData, routingNumber: e.target.value })}
                className="w-full input-compact"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Account Number</label>
              <input
                type="text"
                value={formData.accountNumber}
                onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
                className="w-full input-compact"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Account Type</label>
              <select
                value={formData.accountType}
                onChange={(e) => setFormData({ ...formData, accountType: e.target.value })}
                className="w-full input-compact"
              >
                <option value="CHECKING">Checking</option>
                <option value="SAVINGS">Savings</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Digital Platform</label>
              <input
                type="text"
                value={formData.digitalPlatform}
                onChange={(e) => setFormData({ ...formData, digitalPlatform: e.target.value })}
                className="w-full input-compact"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Digital Handle</label>
              <input
                type="text"
                value={formData.digitalHandle}
                onChange={(e) => setFormData({ ...formData, digitalHandle: e.target.value })}
                className="w-full input-compact"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium mb-1">Package Type *</label>
              <select
                value={formData.packageType}
                onChange={(e) => setFormData({ ...formData, packageType: e.target.value })}
                className="w-full input-compact"
              >
                <option value="BASIC">Basic</option>
                <option value="STANDARD">Standard</option>
                <option value="PREMIUM">Premium</option>
                <option value="MAXIMUM">Maximum</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Deposit Amount *</label>
              <input
                type="number"
                required
                value={formData.depositAmount}
                onChange={(e) => setFormData({ ...formData, depositAmount: e.target.value })}
                className="w-full input-compact"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Service Fee *</label>
              <input
                type="number"
                required
                value={formData.serviceFee}
                onChange={(e) => setFormData({ ...formData, serviceFee: e.target.value })}
                className="w-full input-compact"
              />
            </div>
            <div className="flex items-center gap-2 mt-6">
              <input
                type="checkbox"
                checked={formData.rushAddon}
                onChange={(e) => setFormData({ ...formData, rushAddon: e.target.checked })}
              />
              <label className="text-sm">Rush Addon</label>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-mid-gray rounded hover:bg-light-gray"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Create Client'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
